/**
 * Anonymous → account funnel.
 *
 * Fans start browsing without accounts. After they've done a couple of
 * meaningful things (played 2 Fan Zone games OR poked 2 interactive
 * widgets) we soft-prompt them for an email + newsletter opt-in — the
 * only backend "account" we support today.
 *
 * State lives in localStorage so the counter survives reloads. The
 * modal dispatches through a browser CustomEvent so any component can
 * trigger the counter without importing the modal itself.
 */
const STORAGE_KEY = "titan_engagement_v1";
const EVENT_NAME = "titan:engagement-bumped";
const THRESHOLD = 2;                     // 2 games OR 2 widgets
export const ENGAGEMENT_EVENT = EVENT_NAME;

function _read() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function _write(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota errors */
  }
}

/** Read-only snapshot for the modal to decide whether to show. */
export function getEngagement() {
  const s = _read();
  return {
    games: Number(s.games || 0),
    widgets: Number(s.widgets || 0),
    dismissed: !!s.dismissed,
    signed_up: !!s.signed_up,
  };
}

/**
 * Bump the counter for one kind of engagement. De-dupes on `dedupeKey`
 * (per calendar-day by default) so, e.g., replaying the same trivia
 * doesn't count 100 times.
 */
export function bumpEngagement(kind, { dedupeKey } = {}) {
  if (!["game", "widget"].includes(kind)) return;
  const s = _read();
  const today = new Date().toISOString().slice(0, 10);
  const dedupe = dedupeKey || `${kind}:${today}`;
  const seen = s._seen || {};
  if (seen[dedupe]) return;
  seen[dedupe] = 1;
  s._seen = seen;
  const bucket = kind === "game" ? "games" : "widgets";
  s[bucket] = Number(s[bucket] || 0) + 1;
  _write(s);
  try {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: getEngagement() }));
  } catch {
    /* SSR / non-browser */
  }
}

/** Should we auto-prompt right now? */
export function shouldPrompt() {
  const e = getEngagement();
  if (e.signed_up || e.dismissed) return false;
  return e.games >= THRESHOLD || e.widgets >= THRESHOLD;
}

/** Mark the user as signed up / dismissed so we don't re-prompt. */
export function markSignedUp() {
  const s = _read();
  s.signed_up = true;
  _write(s);
}

export function dismissPrompt() {
  const s = _read();
  s.dismissed = true;
  _write(s);
}
